import React from "react";
import { Pill, SectionLabel, Card } from "./ui";

const LAYERS = [
  { key: "perceive", label: "Perceive", tag: "01",
    desc: "Reads every signal that matters — invoices, tickets, ERP events, WhatsApp, voice, IoT, vision.",
    examples: ["ERP · CRM", "Email · chat", "Voice · vision", "Sensors · APIs"] },
  { key: "reason", label: "Reason", tag: "02",
    desc: "Plans the next best action using policies you co-design, frontier reasoning models, and live business context.",
    examples: ["Multi-model routing", "Policy + guardrails", "Plan / replan", "Long-horizon memory"] },
  { key: "act", label: "Act", tag: "03",
    desc: "Executes real work — files claims, ships orders, sends messages, books trucks, posts journals.",
    examples: ["200+ tool connectors", "Human-in-the-loop", "Audit trail", "Rollback safe"] },
  { key: "learn", label: "Learn", tag: "04",
    desc: "Every outcome updates the agent. Policies it discovers become reusable — across your whole org.",
    examples: ["Outcome scoring", "Policy distillation", "Drift detection", "Self-evals"] },
];

export default function AgentAnatomy() {
  return (
    <section id="agents" className="relative py-24 lg:py-32">
      <div className="pointer-events-none absolute inset-0 bg-grid-faint opacity-60" />
      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <div className="max-w-3xl">
          <SectionLabel tone="accent">Anatomy of a self-learning agent</SectionLabel>
          <h2 className="mt-5 text-[36px] leading-[1.05] tracking-tight sm:text-[44px] lg:text-[54px]">
            <span className="text-white">Not a chatbot.</span>{" "}
            <span className="font-display italic grad-text">An employee that compounds.</span>
          </h2>
          <p className="mt-5 max-w-2xl text-[16.5px] leading-relaxed text-white/65">
            Generic AI answers. EkkBaz agents <span className="text-white">close the loop</span>: they perceive your business, reason through ambiguity, take real action against your systems, and get measurably better at it every week — because they remember what worked.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-4 gap-4">
          {LAYERS.map((l, i) => (
            <div key={l.key} className="relative">
              <Card className="relative h-full p-6 hover:border-white/15">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/35">step / {l.tag}</span>
                  <span className="font-mono text-[10px] text-cyan-300/80">{i === 3 ? "↺ loop" : "→"}</span>
                </div>
                <h3 className="mt-4 text-[22px] font-semibold tracking-tight text-white">{l.label}</h3>
                <p className="mt-2 text-[14px] leading-relaxed text-white/65">{l.desc}</p>
                <div className="mt-5 flex flex-wrap gap-1.5">
                  {l.examples.map(e => (
                    <span key={e} className="rounded border border-white/[0.08] bg-white/[0.03] px-2 py-1 font-mono text-[10.5px] text-white/55">{e}</span>
                  ))}
                </div>
                <div className="mt-6 h-1 w-full overflow-hidden rounded-full bg-white/[0.05]">
                  <div className="h-full rounded-full" style={{
                    width: ["28%","52%","76%","100%"][i],
                    background: "linear-gradient(90deg, rgba(94,234,212,0.4), rgba(76,125,255,0.9))"
                  }} />
                </div>
              </Card>
              {i < 3 && (
                <div className="hidden lg:block absolute top-1/2 -right-3 -translate-y-1/2 z-10">
                  <div className="h-7 w-7 rounded-full border border-white/15 bg-[#070d24] flex items-center justify-center text-cyan-300">
                    <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-10 grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-white/[0.06] bg-white/[0.04] lg:grid-cols-4">
          {[
            { k: "Self-learning", v: "Improves weekly without retraining" },
            { k: "Autonomous", v: "Decides + executes, not just suggests" },
            { k: "Custom", v: "Built on your data, your tools, your rules" },
            { k: "Auditable", v: "Every action traced. Every policy explainable." },
          ].map(x => (
            <div key={x.k} className="bg-[#070d24] p-5">
              <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-cyan-300/80">{x.k}</div>
              <div className="mt-1 text-[15px] text-white/85 leading-snug">{x.v}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
